import { MouseEvent, type ReactElement, useRef } from 'react';
import type { Table } from '@tanstack/react-table';

import { Checkbox } from '@crossnokaye/ux-common/ckds';

interface SelectHeaderProps<TData> {
  table: Table<TData>;
  /**
   * Controls the header checkbox behavior for bulk selection.
   *
   * - `'all'` — Full select-all checkbox (default)
   * - `'deselect-only'` — Disabled when empty, indeterminate when selected, always deselects
   * - `'none'` — No header checkbox
   */
  selectAllMode?: 'all' | 'deselect-only' | 'none';
}

/**
 * Header checkbox for bulk selection.
 *
 * Displays three states:
 * - Checked: All rows are selected
 * - Unchecked: No rows are selected
 * - Indeterminate: Some rows are selected
 *
 * When selectAllMode is 'deselect-only', operates in deselect-only mode:
 * disabled when nothing is selected, indeterminate when any rows are selected,
 * and clicking always deselects all.
 */
export const SelectHeader = <TData,>({
  table,
  selectAllMode = 'all',
}: SelectHeaderProps<TData>): ReactElement | null => {
  const ref = useRef<HTMLButtonElement>(null);

  if (selectAllMode === 'none') {
    return null;
  }

  const isAllSelected = table.getIsAllRowsSelected();
  const isSomeSelected = table.getIsSomeRowsSelected();
  const isDeselectOnly = selectAllMode === 'deselect-only';
  const hasAnySelected = isAllSelected || isSomeSelected;

  // Determine checkbox state
  let checkedState: boolean | 'indeterminate';
  if (isDeselectOnly) {
    // Deselect-only: never show fully checked, show indeterminate when any selected
    checkedState = hasAnySelected ? 'indeterminate' : false;
  } else {
    checkedState = isAllSelected ? true : isSomeSelected ? 'indeterminate' : false;
  }

  const handleCellClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isDeselectOnly && !hasAnySelected) return;
    ref.current?.click();
  };

  return (
    <div
      className="flex items-center justify-center w-full h-full"
      onMouseDown={(e) => {
        // Prevent table drag-scroll from treating checkbox clicks as drag starts.
        e.stopPropagation();
      }}
      onPointerDown={(e) => {
        // Keep behavior consistent for pointer events (touch/pen/mouse).
        e.stopPropagation();
      }}
      onClick={handleCellClick}
      role="button"
    >
      <Checkbox
        ref={ref}
        checked={checkedState}
        disabled={isDeselectOnly && !hasAnySelected}
        onCheckedChange={(checked) => {
          if (isDeselectOnly) {
            // Deselect-only mode: always deselect all
            table.toggleAllRowsSelected(false);
          } else {
            // Normal mode: toggle based on checked state
            table.toggleAllRowsSelected(checked === true);
          }
        }}
        aria-label={isDeselectOnly ? 'Deselect all rows' : 'Select all rows'}
      />
    </div>
  );
};

SelectHeader.displayName = 'SelectHeader';
